// "Scan a code": reads the guardian app's rotating pass from the warm camera, about 5 times a
// second, and hands the text to the flow, which verifies it offline with the site key.
import { useEffect, useRef, useState } from "react";
import { Banner, Button, ScannerFrame, Spinner } from "@vavr/ui";
import type { QrDecoder } from "../qr-scan.ts";
import { LiveVideo } from "./StaffStrip.tsx";

export interface QrScannerProps {
  stream: MediaStream | null;
  video: HTMLVideoElement | null;
  makeDecoder: () => Promise<QrDecoder>;
  onText: (text: string) => Promise<void> | void;
  onClose: () => void;
  error: string | null;
}

export function QrScanner({ stream, video, makeDecoder, onText, onClose, error }: QrScannerProps) {
  const [ready, setReady] = useState(false);
  const busy = useRef(false);
  const lastText = useRef<string | null>(null);

  useEffect(() => {
    if (!video) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    makeDecoder().then((decode) => {
      if (!live) return;
      setReady(true);
      const tick = async () => {
        if (!live) return;
        if (!busy.current) {
          const text = await decode(video).catch(() => null);
          // The same frame text is not re-verified while it stays on screen.
          if (text && text !== lastText.current && live) {
            lastText.current = text;
            busy.current = true;
            try { await onText(text); } finally { busy.current = false; }
          }
        }
        timer = setTimeout(tick, 200);
      };
      tick();
    });
    return () => { live = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video]);

  return (
    <ScannerFrame
      className="pmk-scan" shape="wide"
      title="Hold your phone's code up to the camera"
      hint="Open Vavr, tap My code, and hold the phone steady."
      overlay={!ready && video && <Spinner size="lg" label="Starting the scanner" />}
      status={<>
        {!video && <Banner tone="info" title="The camera is not available">Please find your child by name instead.</Banner>}
        {error && <Banner tone="warning" title={error} />}
      </>}
      actions={<Button variant="secondary" size="lg" icon="search" onClick={onClose}>Find by name instead</Button>}
    >
      <LiveVideo stream={stream} label="Camera view for scanning" className="pmk-video--mirror" />
    </ScannerFrame>
  );
}
